import React, { useState } from 'react';
import { ChevronDown, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../components/ui/dropdown-menu';
import { projectsApi } from '../api';
import { STATUS_OPTIONS, STATUS_STYLES, statusLabel } from '../constants';

// Clickable project status badge — change status inline anywhere in Ops.
export const ProjectStatusBadge = ({ project, onChanged, disabled = false, size = 'md' }) => {
  const [saving, setSaving] = useState(false);
  const sizeCls = size === 'sm' ? 'text-[10px] px-2 py-0.5' : 'text-xs px-2.5 py-1';
  const badgeCls = `inline-flex items-center gap-1 font-medium rounded-full border ${sizeCls} ${
    STATUS_STYLES[project.status] || 'bg-gray-700 text-gray-300 border-gray-600'
  }`;

  if (disabled) {
    return <span className={badgeCls}>{statusLabel(project.status)}</span>;
  }

  const changeStatus = async (status) => {
    if (status === project.status || saving) return;
    setSaving(true);
    try {
      const res = await projectsApi.update(project.id, { status });
      toast.success(`Status changed to ${statusLabel(status)}`);
      onChanged?.(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not change status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className={`${badgeCls} cursor-pointer hover:brightness-125 transition-all`}
          title="Click to change status"
          data-testid={`status-badge-${project.id}`}
        >
          {saving ? <Loader2 size={11} className="animate-spin" /> : null}
          {statusLabel(project.status)}
          <ChevronDown size={11} className="opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="bg-[#161616] border-gray-800 text-white z-50"
        onClick={(e) => e.stopPropagation()}
      >
        {STATUS_OPTIONS.map((o) => (
          <DropdownMenuItem
            key={o.value}
            onClick={(e) => { e.stopPropagation(); changeStatus(o.value); }}
            className="cursor-pointer focus:bg-[#1F1F1F] focus:text-white"
            data-testid={`status-option-${o.value}`}
          >
            <span className={`h-2 w-2 rounded-full mr-2 ${STATUS_STYLES[o.value]?.split(' ')[0] || 'bg-gray-600'}`} style={{ padding: 4 }} />
            {o.label}
            {o.value === project.status && <Check size={13} className="ml-auto text-[#D4AF37]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
